import React, { useState, useEffect, useRef } from "react";
import { Form, Checkbox, Dropdown, Label } from "semantic-ui-react";
import PropTypes from "prop-types";
import { isEmpty, get, isEqual, map } from "lodash";
import Joi from "joi";

import { connect } from "react-redux";
import genericAction from "@Actions/generic";

import { getTimeLabel } from "@Utils/functionality";

import IKModal from "@Components/IkModal";
import Input from "@Components/Input";
import TextAreaInput from "@Components/TextArea";
import ConfirmModal from "@Modals/ConfirmModal";
import styles from "./styles.module.css";

const SurveyCrud = ({ dispatch, onClose, data, modalOption, currentCompanyId }) => {
	const [isActive, setIsActive] = useState(false);
	const [xpressiSurveyId, setXpressiSurveyId] = useState("");
	const [xpressiSurveyName, setXpressiSurveyName] = useState("");
	const [companyId, setCompanyId] = useState("");
	const [flowId, setFlowId] = useState("");
	const [header, setHeader] = useState("");
	const [body, setBody] = useState("");
	const [footer, setFooter] = useState("");
	const [buttonCaption, setButtonCaption] = useState("");
	const [type, setType] = useState("");

	const [errors, setErrors] = useState({
		flowId: "",
		body: "",
		buttonCaption: "",
		type: "",
	});

	const itemEditSurvey = get(data, "survey", {});
	const initialMode = modalOption || (isEmpty(itemEditSurvey) ? "Add" : "Watch");
	const [mode, setMode] = useState(initialMode);
	const hasLoadedData = useRef(false);
	const currentSurveyId = useRef(null);
	const [open, setOpen] = useState(false);
	const [initialFormData, setInitialFormData] = useState({});
	const [surveyOptions, setSurveyOptions] = useState([]);
	const [hasActiveSurvey, setHasActiveSurvey] = useState(false);
	const [activeSurveyId, setActiveSurveyId] = useState(null);

	useEffect(() => {
		const newMode = modalOption || (isEmpty(itemEditSurvey) ? "Add" : "Watch");
		setMode(newMode);
	}, [modalOption, itemEditSurvey]);

	useEffect(() => {
		(async () => {
			const [surveysData, surveysResponse] = await Promise.all([
				dispatch(
					genericAction({
						method: "get",
						route: "/surveys/xpressi",
					})
				),
				dispatch(
					genericAction({
						method: "get",
						route: "surveys",
					})
				),
			]);

			setSurveyOptions(
				map(surveysData, (item) => {
					const surveyId = get(item, "id", "");
					const surveyName = get(item, "title", "") || `Encuesta ${surveyId}`;
					const surveyCreatedDateLabel = getTimeLabel(get(item, "createdDate", ""), "DD/MM/YYYY");
					const searchText = `${surveyId} - ${surveyName} - Creada ${surveyCreatedDateLabel}`;

					return {
						key: surveyId,
						value: surveyId,
						text: (
							<span className={styles.optionText}>
								<span className={styles.surveyBadge}>{surveyId}</span>
								&nbsp;
								{surveyName} - Creada {surveyCreatedDateLabel}
							</span>
						),
						content: (
							<span className={styles.optionText}>
								<span className={styles.surveyBadge}>{surveyId}</span>
								&nbsp;
								{surveyName} - Creada {surveyCreatedDateLabel}
							</span>
						),
						searchText,
						surveyName,
					};
				})
			);

			const surveys = get(surveysResponse, "items", []) || surveysResponse || [];
			const activeSurvey = surveys.find((survey) => {
				return get(survey, "isActive", false) === true;
			});

			if (activeSurvey) {
				setHasActiveSurvey(true);
				setActiveSurveyId(get(activeSurvey, "id", null));
			} else {
				setHasActiveSurvey(false);
				setActiveSurveyId(null);
			}
		})();
	}, [dispatch]);

	const schema = Joi.object({
		flowId: Joi.string().required().messages({
			"string.empty": "Flow ID es requerido",
		}),
		body: Joi.string().required().messages({
			"string.empty": "Msj. a Enviar es requerido",
		}),
		buttonCaption: Joi.string().required().messages({
			"string.empty": "Nombre Botón es requerido",
		}),
		type: Joi.string().required().messages({
			"string.empty": "Tipo Envío es requerido",
			"any.required": "Tipo Envío es requerido",
		}),
	});

	useEffect(() => {
		const surveyIdFromData = get(itemEditSurvey, "id");

		if (currentSurveyId.current !== surveyIdFromData || !hasLoadedData.current) {
			hasLoadedData.current = false;
			currentSurveyId.current = surveyIdFromData;
		}

		(async () => {
			if ((mode === "Edit" || mode === "Watch") && surveyIdFromData && !hasLoadedData.current) {
				hasLoadedData.current = true;
				const surveyData = await dispatch(
					genericAction({
						method: "get",
						route: `surveys/${surveyIdFromData}`,
					})
				);

				if (!isEmpty(surveyData)) {
					const formData = {
						isActive: get(surveyData, "isActive", true),
						xpressiSurveyId: get(surveyData, "xpressiSurveyId", "") ? String(get(surveyData, "xpressiSurveyId", "")) : "",
						xpressiSurveyName: get(surveyData, "xpressiSurveyName", "") || "",
						companyId: get(surveyData, "companyId", "") ? String(get(surveyData, "companyId", "")) : String(currentCompanyId || ""),
						flowId: get(surveyData, "flowId", ""),
						header: get(surveyData, "header", ""),
						body: get(surveyData, "body", ""),
						footer: get(surveyData, "footer", ""),
						buttonCaption: get(surveyData, "buttonCaption", ""),
						type: get(surveyData, "type", "") ? get(surveyData, "type").toLowerCase() : "",
					};

					setIsActive(formData.isActive);
					setXpressiSurveyId(formData.xpressiSurveyId);
					setXpressiSurveyName(formData.xpressiSurveyName);
					setCompanyId(formData.companyId);
					setFlowId(formData.flowId);
					setHeader(formData.header);
					setBody(formData.body);
					setFooter(formData.footer);
					setButtonCaption(formData.buttonCaption);
					setType(formData.type);
					setInitialFormData(formData);
				}
			} else if (mode === "Add") {
				hasLoadedData.current = true;
				const emptyFormData = {
					isActive: false,
					xpressiSurveyId: "",
					xpressiSurveyName: "",
					companyId: String(currentCompanyId || ""),
					flowId: "",
					header: "",
					body: "",
					footer: "",
					buttonCaption: "",
					type: "",
				};
				setIsActive(emptyFormData.isActive);
				setXpressiSurveyId(emptyFormData.xpressiSurveyId);
				setXpressiSurveyName(emptyFormData.xpressiSurveyName);
				setCompanyId(emptyFormData.companyId);
				setFlowId(emptyFormData.flowId);
				setHeader(emptyFormData.header);
				setBody(emptyFormData.body);
				setFooter(emptyFormData.footer);
				setButtonCaption(emptyFormData.buttonCaption);
				setType(emptyFormData.type);
				setInitialFormData(emptyFormData);
			}
		})();
	}, [mode, dispatch, itemEditSurvey, currentCompanyId]);

	const handleSaveSurvey = async () => {
		const validation = schema.validate({ flowId, body, buttonCaption, type }, { abortEarly: false });
		if (validation.error) {
			const errorsObj = validation.error.details.reduce(
				(acc, item) => {
					acc[item.context.key] = item.message;
					return acc;
				},
				{ flowId: "", body: "", buttonCaption: "", type: "" }
			);
			setErrors(errorsObj);
			return;
		}

		const flowData = {
			companyId: companyId ? parseInt(companyId, 10) : currentCompanyId,
			xpressiSurveyId: xpressiSurveyId ? parseInt(xpressiSurveyId, 10) : null,
			xpressiSurveyName: xpressiSurveyName || "xPressi Default",
			flowId,
			header: header || null,
			body,
			footer: footer || null,
			buttonCaption,
			type: type === "flow" ? "Flow" : type === "web" ? "Web" : type,
			isActive,
		};

		if (mode === "Add") {
			const dataAdd = await dispatch(
				genericAction({
					method: "post",
					route: "surveys",
					body: flowData,
				})
			);
			if (!isEmpty(dataAdd)) {
				const emptyFormData = {
					isActive: false,
					xpressiSurveyId: "",
					xpressiSurveyName: "",
					companyId: String(currentCompanyId || ""),
					flowId: "",
					header: "",
					body: "",
					footer: "",
					buttonCaption: "",
					type: "",
				};
				setInitialFormData(emptyFormData);
				dispatch(
					genericAction({
						method: "get",
						route: "surveys",
					})
				).then((surveysResponse) => {
					const surveys = get(surveysResponse, "items", []) || surveysResponse || [];
					const activeSurvey = surveys.find((survey) => {
						return get(survey, "isActive", false) === true;
					});
					if (activeSurvey) {
						setHasActiveSurvey(true);
						setActiveSurveyId(get(activeSurvey, "id", null));
					} else {
						setHasActiveSurvey(false);
						setActiveSurveyId(null);
					}
				});
				onClose(true);
			}
		} else if (mode === "Edit") {
			const editSurveyId = get(itemEditSurvey, "id", "");
			const dataEdit = await dispatch(
				genericAction({
					method: "patch",
					route: `surveys/${editSurveyId}`,
					body: flowData,
				})
			);
			if (!isEmpty(dataEdit)) {
				const currentFormData = {
					isActive,
					xpressiSurveyId,
					xpressiSurveyName,
					companyId,
					flowId,
					header,
					body,
					footer,
					buttonCaption,
					type,
				};
				setInitialFormData(currentFormData);
				dispatch(
					genericAction({
						method: "get",
						route: "surveys",
					})
				).then((surveysResponse) => {
					const surveys = get(surveysResponse, "items", []) || surveysResponse || [];
					const activeSurvey = surveys.find((survey) => {
						return get(survey, "isActive", false) === true;
					});
					if (activeSurvey) {
						setHasActiveSurvey(true);
						setActiveSurveyId(get(activeSurvey, "id", null));
					} else {
						setHasActiveSurvey(false);
						setActiveSurveyId(null);
					}
				});
				onClose(true);
			}
		}
	};

	const cancelModal = () => {
		onClose();
	};

	const modalOpen = () => {
		const currentFormData = {
			isActive,
			xpressiSurveyId,
			xpressiSurveyName,
			companyId,
			flowId,
			header,
			body,
			footer,
			buttonCaption,
			type,
		};

		if (mode === "Add") {
			const isFormEmpty = !xpressiSurveyId && !xpressiSurveyName && !flowId && !header && !body && !footer && !buttonCaption && !type;

			if (isFormEmpty) {
				cancelModal();
			} else if (open) {
				setOpen(false);
			} else {
				setOpen(true);
			}
		} else if (isEqual(initialFormData, currentFormData)) {
			cancelModal();
		} else if (open) {
			setOpen(false);
		} else {
			setOpen(true);
		}
	};

	const handleTypeChange = (e, { value }) => {
		setType(value);
		setErrors({ ...errors, type: "" });
	};

	const handleXpressiSurveyChange = (e, { value }) => {
		const option = surveyOptions.find((item) => {
			return item.value === value;
		});

		setXpressiSurveyId(value ? String(value) : "");
		setXpressiSurveyName(option ? option.surveyName || "xPressi Default" : "xPressi Default");
	};

	const searchXpressi = (options, query) => {
		const q = (query || "").toLowerCase();
		return options.filter((opt) => {
			const text = (opt.searchText || "").toLowerCase();
			return text.includes(q);
		});
	};

	const typeOptions = [
		{ key: 1, text: "Flow", value: "flow" },
		{ key: 2, text: "Web", value: "web" },
	];

	return (
		<>
			<IKModal
				title={
					mode === "Add"
						? "Configuración"
						: `Encuesta: ${header || get(itemEditSurvey, "header", "")} - Id: ${get(itemEditSurvey, "id", "")}`
				}
				mode={mode}
				setMode={setMode}
				size="tiny"
				onClose={modalOpen}
				onSuccess={handleSaveSurvey}
				fullHeight>
				<div className={styles.formContainer}>
					<Form>
						<div className={styles.divActiveCompany}>
							<Form.Field className={styles.divActiveCompanys}>
								<span className={styles.span}>Compañía ID:</span>
								<Input
									icon="building"
									name="companyId"
									type="number"
									fluid
									width={10}
									value={companyId}
									disabled
									onChange={(e) => {
										setCompanyId(e.target.value);
									}}
								/>
							</Form.Field>

							<Form.Field className={styles.divActive}>
								<span className={styles.span}>Activo:</span>
								<Checkbox
									toggle
									label={isActive ? "Si" : "No"}
									checked={isActive}
									disabled={mode === "Watch" || (hasActiveSurvey && activeSurveyId !== get(itemEditSurvey, "id", null))}
									onChange={(e, { checked }) => {
										setIsActive(checked);
									}}
								/>
							</Form.Field>
						</div>

						<Form.Field className={styles.divSurveyId}>
							<span className={styles.spanE}>Encuesta xPressi:</span>
							<Dropdown
								style={{ minHeight: "35px" }}
								fluid
								selection
								clearable
								search={searchXpressi}
								placeholder="Seleccionar encuesta"
								name="xpressiSurveyId"
								options={surveyOptions}
								onChange={handleXpressiSurveyChange}
								value={xpressiSurveyId ? parseInt(xpressiSurveyId, 10) : null}
								disabled={mode === "Watch"}
							/>
						</Form.Field>

						<div className={styles.divFlowIdTipoEnvio}>
							<Form.Field className={styles.divFlowId}>
								<span className={styles.spanE}>Flow ID:</span>
								<Input
									icon="linkify"
									placeholder="Escribe el Número..."
									name="flowId"
									value={flowId}
									required
									disabled={mode === "Watch"}
									onChange={(e) => {
										setFlowId(e.target.value);
										setErrors({ ...errors, flowId: "" });
									}}
									error={!isEmpty(errors.flowId) && { content: errors.flowId, pointing: "above" }}
								/>
							</Form.Field>

							<Form.Field className={styles.divTipoEnvio} required>
								<span className={styles.span}>
									Tipo Envío: <span className={styles.requiredAsterisk}>*</span>
								</span>
								<Dropdown
									placeholder="Seleccionar"
									fluid
									clearable
									search
									selection
									value={type}
									name="type"
									required
									disabled={mode === "Watch"}
									onChange={handleTypeChange}
									error={!isEmpty(errors.type)}
									options={typeOptions}
								/>
								{!isEmpty(errors.type) && (
									<Label basic color="red" pointing prompt>
										{errors.type}
									</Label>
								)}
							</Form.Field>
						</div>

						<Form.Field className={styles.divHeader}>
							<Input
								label="Título:"
								icon="heading"
								placeholder="Escribe aqui..."
								name="header"
								value={header}
								disabled={mode === "Watch"}
								onChange={(e) => {
									setHeader(e.target.value);
								}}
							/>
						</Form.Field>

						<Form.Field className={styles.divHeader}>
							<TextAreaInput
								label="Msj. a Enviar:"
								name="body"
								value={body}
								showRequired
								disabled={mode === "Watch"}
								onChange={(e) => {
									setBody(e.target.value);
									setErrors({ ...errors, body: "" });
								}}
								error={!isEmpty(errors.body) ? errors.body : null}
								minRows={2}
							/>
						</Form.Field>

						<Form.Field className={styles.divHeader}>
							<Input
								label="Pie:"
								icon="align center"
								placeholder="Escribe aqui..."
								name="footer"
								value={footer}
								disabled={mode === "Watch"}
								onChange={(e) => {
									setFooter(e.target.value);
								}}
							/>
						</Form.Field>

						<Form.Field className={styles.divHeader}>
							<Input
								label="Nombre Botón:"
								placeholder="Escribe aqui..."
								icon="hand pointer"
								name="buttonCaption"
								value={buttonCaption}
								required
								disabled={mode === "Watch"}
								onChange={(e) => {
									setButtonCaption(e.target.value);
									setErrors({ ...errors, buttonCaption: "" });
								}}
								error={!isEmpty(errors.buttonCaption) && { content: errors.buttonCaption, pointing: "above" }}
							/>
						</Form.Field>
					</Form>
				</div>
			</IKModal>

			{open && (
				<ConfirmModal
					visible
					icon="exclamation circle"
					header="Confirmar Salir"
					buttonLabel="Salir"
					fullContent="Tienes cambios sin guardar. ¿Estás seguro que deseas salir?"
					onConfirm={() => {
						setOpen(false);
						cancelModal();
					}}
					onDismiss={() => {
						setOpen(false);
					}}
				/>
			)}
		</>
	);
};

SurveyCrud.propTypes = {
	dispatch: PropTypes.func.isRequired,
	onClose: PropTypes.func.isRequired,
	data: PropTypes.object,
	modalOption: PropTypes.string,
	currentCompanyId: PropTypes.number,
};

export default connect()(SurveyCrud);
